import { SwatchIcon, TagIcon, UserGroupIcon } from '@heroicons/react/20/solid';
import { useEffect, useRef, useState } from 'react';
import { GenericWorksheet, ReconciliationWorksheet } from '../../../../types/worksheet';
import QuickInfo from '../../QuickInfo';
import Assigned from '../Assigned';
import { WorksheetStatusIcon } from '../../WorksheetStatusIcon';
import Currency from '../../../../components/currency/Currency';
import TopActions from '../top-actions/TopActions';
import { isNullOrUndefined } from '../../../new-workpaper/utils';
import { TooltipV2 } from '@cimplico/components';
import { formatDateTime } from '../../../../utilities/formatting';
import { useWorkpaperContext } from '../../../../providers/WorkpaperProvider';
import XeroLogo from '../../../../assets/xero.png';

type ScrollHeaderProps = {
    worksheet: ReconciliationWorksheet | GenericWorksheet;
    onXeroAuthError?: () => void;
};

/**
 * This component renders a minified view of the worksheet details to remain in view
 * while the user scrolls down the page.
 *
 * Users have a subset of quick actions they are still able to perform while in this view.
 *
 * @param ScrollHeaderProps
 * @returns
 */
function ScrollHeader({ worksheet, onXeroAuthError }: ScrollHeaderProps): JSX.Element {
    const [isAtTop, setIsAtTop] = useState(false);
    const header = useRef<HTMLDivElement | null>(null);
    const workpaper = useWorkpaperContext();
    const { account } = worksheet;
    const unreconciledCr = 'unreconciledCr' in worksheet ? worksheet.unreconciledCr : undefined;
    const unreconciledDr = 'unreconciledDr' in worksheet ? worksheet.unreconciledDr : undefined;
    const unReconciledAmount = isNullOrUndefined(unreconciledDr) ? unreconciledCr : unreconciledDr;
    const reconciled = !unreconciledDr && !unreconciledCr;

    // This is the number of pixels from the top of the page to be considered at the 'top' of the container
    const pixelsFromPageTop = 76;

    /**
     * This effect sets up a scroll event listener on the window object.
     * It calculates the distance of a specific element (header.current) from the top of the page.
     * Based on this distance, it updates a state variable (isAtTop) to indicate whether the element
     * is within a certain threshold distance (pixelsFromPageTop) from the top of the page.
     * This functionality is used to show or hide a sticky header as the user scrolls.
     */
    useEffect(() => {
        if (!header.current) {
            return;
        }

        const elDistanceToTop = window.pageYOffset + header.current.getBoundingClientRect().top;
        setIsAtTop(elDistanceToTop <= pixelsFromPageTop);

        function handleScroll(): void {
            if (!header.current) {
                return;
            }
            const elDistanceToTop = window.pageYOffset + header.current.getBoundingClientRect().top;
            setIsAtTop(elDistanceToTop <= pixelsFromPageTop);
        }
        window.addEventListener('scroll', handleScroll, true);
        return (): void => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div ref={header} className={`sticky top-0 z-[45] ${isAtTop ? 'bg-white' : 'invisible h-0'}`}>
            <div className="flex items-center justify-between pb-3 shrink-0 overflow-x-auto absolute bg-white border-b w-full">
                <div className="flex flex-col shrink-0">
                    {/* Status and Name */}
                    <div className="flex items-center">
                        <WorksheetStatusIcon status={worksheet.status} />
                        <p className="font-medium text-gray-800 ml-0.5">{worksheet.name}</p>
                    </div>
                    {/* Supplementary details and Assigned */}
                    <div className="flex gap-2 items-center shrink-0 flex-wrap ">
                        {/* Account type */}
                        {account && (
                            <>
                                {account.provider === 'XERO' && (
                                    <>
                                        <TooltipV2
                                            disabled={!workpaper.trialBalanceRefreshedAt}
                                            message={'Last refreshed:' + formatDateTime(workpaper.trialBalanceRefreshedAt || '')}
                                            delayMs={750}
                                        >
                                            <img src={XeroLogo} className="h-6 w-6 shrink-0" />
                                        </TooltipV2>
                                        <div className="h-1 w-1 bg-gray-500 rounded-full"></div>
                                    </>
                                )}
                                <QuickInfo label={account.type} icon={<SwatchIcon />} />
                                <div className="h-1 w-1 bg-gray-500 rounded-full"></div>
                            </>
                        )}
                        {/* Worksheet tag */}
                        {worksheet.tag && (
                            <>
                                <QuickInfo label={worksheet.tag} icon={<TagIcon />} />
                                <div className="h-1 w-1 bg-gray-500 rounded-full"></div>
                            </>
                        )}
                        {/* Assigned */}
                        <div className="flex gap-2">
                            <UserGroupIcon className="text-gray-500 h-5 w-5 inline" />
                            <Assigned worksheet={worksheet} />
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-4 shrink-0 ">
                    {/* Account details */}
                    {account && (
                        <>
                            {/* Current Amount */}
                            <div className="flex flex-col items-end">
                                <p className="text-gray-500 text-sm">Current amount</p>
                                <Currency
                                    className="text-indigo-700 text-sm font-medium"
                                    value={account.workpaperDebitTY ?? account.workpaperCreditTY}
                                    withParentheses={account.workpaperCreditTY !== null}
                                />
                            </div>
                            <div className="w-[1px] bg-gray-200 h-8"></div>
                            {/* Unreconciled amount */}
                            {unReconciledAmount !== undefined && (
                                <>
                                    <div className="flex flex-col items-end">
                                        <label className="text-gray-500 text-sm">Unreconciled Amount</label>
                                        <Currency
                                            value={unReconciledAmount}
                                            className={`text-sm font-medium ${reconciled ? 'text-green-600 ' : 'text-red-600 '}`}
                                            withParentheses={!isNullOrUndefined(unreconciledCr)}
                                        />
                                    </div>
                                    <div className="w-[1px] bg-gray-200 h-8"></div>
                                </>
                            )}
                        </>
                    )}
                    {/* Actions */}
                    <TopActions worksheet={worksheet} hideTransactionsLink separatorHeight={32} onXeroAuthError={onXeroAuthError} />
                </div>
            </div>
        </div>
    );
}

export default ScrollHeader;
